// CalendarGrid — calendar grid for the schedule view. Renders one of
// three layouts driven by `zoom`:
//
//   month (default, back-compat) — period="YYYY-MM"; 7 columns × 6 rows
//     (42 Monday-aligned cells), weekend / out-of-month / today get
//     distinct treatments (brand spec §8.2).
//   week — period="YYYY-MM-DD"; 7 columns × 1 row, the ISO Mon-Sun week
//     containing the given date.
//   day  — period="YYYY-MM-DD"; 1 column × 1 row, exactly that day.
//
// All three share chip placement (max 2 chips per cell), the
// "+ N more" overflow affordance (clickable: opens DayPopover for the
// cell's full activity list), and onActivityOpen on chip click.

import { useMemo, useState } from "react";
import CalendarEventChip from "./CalendarEventChip";
import AggregatedCalendarEventChip from "./AggregatedCalendarEventChip";
import DayPopover from "./DayPopover";
import { type ScheduleKind } from "./kindVocabulary";
import type { Activity, ActivitySeverity } from "../../../types/schedule";
import type { ScheduleZoom } from "../../../hooks/useScheduleUrlState";

interface CalendarGridProps {
  // YYYY-MM for month zoom; YYYY-MM-DD for week / day zoom.
  period: string;
  activities: Activity[];
  onActivityOpen: (activity: Activity) => void;
  today?: Date;
  zoom?: ScheduleZoom;
}

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MAX_CHIPS = 2;

// A day's activities are bucketed by (contract, kind, severity) before
// rendering. Buckets of ≥2 collapse to one AggregatedCalendarEventChip;
// buckets of 1 render as individual CalendarEventChips. A mixed-severity
// day keeps every chip separate so a red activity can never be hidden
// inside an "N × kind" surface. See design doc §3.
type CellEntry =
  | { kind: "single"; activity: Activity }
  | {
      kind: "bucket";
      bucketKey: string;
      kindName: ScheduleKind;
      severity: ActivitySeverity;
      contractRef: string;
      activities: Activity[];
    };

function bucketDayActivities(activities: Activity[]): CellEntry[] {
  const groups = new Map<string, Activity[]>();
  for (const a of activities) {
    const key = `${a.contract.id}::${a.kind}::${a.status}`;
    const list = groups.get(key);
    if (list) list.push(a);
    else groups.set(key, [a]);
  }
  const out: CellEntry[] = [];
  for (const [key, list] of groups) {
    if (list.length >= 2) {
      const first = list[0];
      out.push({
        kind: "bucket",
        bucketKey: key,
        kindName: first.kind as ScheduleKind,
        severity: first.status,
        contractRef: first.contract.ref,
        activities: list,
      });
    } else {
      for (const a of list) {
        out.push({ kind: "single", activity: a });
      }
    }
  }
  return out;
}

interface GridCell {
  iso: string;
  dayNumber: number;
  outside: boolean;
  weekend: boolean;
  isToday: boolean;
}

function isoDay(year: number, month0: number, day: number): string {
  const mm = String(month0 + 1).padStart(2, "0");
  const dd = String(day).padStart(2, "0");
  return `${year}-${mm}-${dd}`;
}

// Monday-first weekday index (0 = Mon … 6 = Sun) for a JS Date.
function mondayIndex(date: Date): number {
  return (date.getUTCDay() + 6) % 7;
}

function parsePeriodDate(period: string): Date {
  // Accepts YYYY-MM or YYYY-MM-DD. Month-only resolves to the first of
  // the month so callers using "2026-05" get a valid Date anchor.
  const [yStr, mStr, dStr] = period.split("-");
  const y = Number(yStr);
  const m0 = Number(mStr) - 1;
  const d = dStr ? Number(dStr) : 1;
  return new Date(Date.UTC(y, m0, d));
}

function buildMonthCells(period: string, todayIso: string): GridCell[] {
  const anchor = parsePeriodDate(period);
  const year = anchor.getUTCFullYear();
  const month0 = anchor.getUTCMonth();
  const first = new Date(Date.UTC(year, month0, 1));
  const start = new Date(first);
  start.setUTCDate(first.getUTCDate() - mondayIndex(first));
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const iso = isoDay(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dow = mondayIndex(d);
    return {
      iso,
      dayNumber: d.getUTCDate(),
      outside: d.getUTCMonth() !== month0,
      weekend: dow >= 5,
      isToday: iso === todayIso,
    };
  });
}

function buildWeekCells(period: string, todayIso: string): GridCell[] {
  // ISO week containing the given date, Mon-first.
  const anchor = parsePeriodDate(period);
  const start = new Date(anchor);
  start.setUTCDate(anchor.getUTCDate() - mondayIndex(anchor));
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(start);
    d.setUTCDate(start.getUTCDate() + i);
    const iso = isoDay(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dow = mondayIndex(d);
    return {
      iso,
      dayNumber: d.getUTCDate(),
      outside: false,
      weekend: dow >= 5,
      isToday: iso === todayIso,
    };
  });
}

function buildDayCells(period: string, todayIso: string): GridCell[] {
  const anchor = parsePeriodDate(period);
  const iso = isoDay(
    anchor.getUTCFullYear(),
    anchor.getUTCMonth(),
    anchor.getUTCDate(),
  );
  const dow = mondayIndex(anchor);
  return [
    {
      iso,
      dayNumber: anchor.getUTCDate(),
      outside: false,
      weekend: dow >= 5,
      isToday: iso === todayIso,
    },
  ];
}

export default function CalendarGrid({
  period,
  activities,
  onActivityOpen,
  today = new Date(),
  zoom = "month",
}: CalendarGridProps) {
  // Popover state carries an optional scoped activity list — the "+N
  // more" overflow path passes the day's full list; the aggregated
  // chip click passes just the bucket's activities. Both reuse the
  // single DayPopover.
  const [popover, setPopover] = useState<{
    iso: string;
    activities: Activity[];
  } | null>(null);

  const todayIso = isoDay(
    today.getUTCFullYear(),
    today.getUTCMonth(),
    today.getUTCDate(),
  );

  const cells = useMemo(() => {
    if (zoom === "week") return buildWeekCells(period, todayIso);
    if (zoom === "day") return buildDayCells(period, todayIso);
    return buildMonthCells(period, todayIso);
  }, [period, todayIso, zoom]);

  // Bucket activities by date once.
  const byDate = useMemo(() => {
    const map = new Map<string, Activity[]>();
    for (const a of activities) {
      const list = map.get(a.date);
      if (list) list.push(a);
      else map.set(a.date, [a]);
    }
    return map;
  }, [activities]);

  // Column layout: 7 for month and week; 1 for day.
  const gridColsClass = zoom === "day" ? "grid-cols-1" : "grid-cols-7";

  // Weekday header row: Mon-Sun for month + week; for day, collapse to
  // the single weekday of the focused day.
  const headerWeekdays = useMemo(() => {
    if (zoom === "day" && cells.length === 1) {
      return [WEEKDAYS[mondayIndex(parsePeriodDate(period))]];
    }
    return WEEKDAYS;
  }, [zoom, cells.length, period]);

  return (
    <>
      <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
        <div className={`grid ${gridColsClass}`}>
          {headerWeekdays.map((wd, i) => (
            <div
              key={`${wd}-${i}`}
              data-testid={`calendar-weekday-${i}`}
              className="border-b border-slate-200 bg-slate-50 px-2 py-1.5 text-overline uppercase text-slate-500 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400"
            >
              {wd}
            </div>
          ))}
          {cells.map((cell) => {
            const dayActivities = byDate.get(cell.iso) ?? [];
            const entries = bucketDayActivities(dayActivities);
            const visible = entries.slice(0, MAX_CHIPS);
            const overflow = entries.length - visible.length;
            return (
              <div
                key={cell.iso}
                data-testid={`calendar-cell-${cell.iso}`}
                data-today={cell.isToday ? "true" : undefined}
                data-outside={cell.outside ? "true" : undefined}
                className={`h-[132px] overflow-hidden border-b border-r border-slate-200 p-1 dark:border-slate-800 ${
                  cell.isToday
                    ? "bg-brand-50/60 dark:bg-brand-950/40"
                    : cell.outside
                      ? "bg-black/[0.18]"
                      : cell.weekend
                        ? "bg-black/[0.10]"
                        : ""
                }`}
              >
                <div className="mb-1 flex justify-end">
                  {cell.isToday ? (
                    <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-brand-600 text-[11px] font-semibold text-white">
                      {cell.dayNumber}
                    </span>
                  ) : (
                    <span
                      className={`px-1 text-caption ${
                        cell.outside
                          ? "text-slate-400 dark:text-slate-600"
                          : "text-slate-500 dark:text-slate-400"
                      }`}
                    >
                      {cell.dayNumber}
                    </span>
                  )}
                </div>
                <div className="space-y-[3px]">
                  {visible.map((entry) =>
                    entry.kind === "single" ? (
                      <CalendarEventChip
                        key={entry.activity.id}
                        activity={entry.activity}
                        selected={false}
                        onClick={() => onActivityOpen(entry.activity)}
                      />
                    ) : (
                      <AggregatedCalendarEventChip
                        key={entry.bucketKey}
                        count={entry.activities.length}
                        kind={entry.kindName}
                        status={entry.severity}
                        contractRef={entry.contractRef}
                        onClick={() =>
                          setPopover({
                            iso: cell.iso,
                            activities: entry.activities,
                          })
                        }
                      />
                    ),
                  )}
                  {overflow > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setPopover({
                          iso: cell.iso,
                          activities: dayActivities,
                        })
                      }
                      className="block px-1 text-[10px] font-medium text-slate-500 hover:text-slate-700 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:text-slate-400 dark:hover:text-slate-200"
                    >
                      + {overflow} more
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {popover && (
        <DayPopover
          isOpen={true}
          date={popover.iso}
          activities={popover.activities}
          onClose={() => setPopover(null)}
          onSelectActivity={(id) => {
            const a = popover.activities.find((x) => x.id === id);
            setPopover(null);
            if (a) onActivityOpen(a);
          }}
        />
      )}
    </>
  );
}
